import clsx from 'clsx';
import { useState, useEffect } from 'react';

import {
  IconMail,
  IconStar,
  IconRepeat,
  IconSchool,
  IconMoodKid,
  IconLanguage,
  IconStarFilled,
  IconFileExport,
  IconGenderFemale,
  IconCalendarEvent,
  IconMessageLanguage,
  IconCircleCheckFilled,
} from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import UserDetailsSkeleton from '@/components/SkeletonLoader/UserDetailsSkeleton';
import { Switch } from '@/components/Switch';
import { TagCapsule } from '@/components/TagCapsule';
import { LessonStageConfig, CommonStatusConfig } from '@/constants/constant';
import {
  EGender,
  EChildRole,
  ERoleAccess,
  ECommonStatus,
  EPersonalSubscriptionType,
} from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import { getChildDetails } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IActivityTimeline, ISubscriptionChildDetails } from '@/types';
import {
  getAssetUrl,
  formatLastActive,
  formatUnixToLongDate,
  formatSecondsToDuration,
} from '@/utils';
import {
  IPdfLabels,
  IPdfStatItem,
  exportChildLearningPdf,
} from '@/utils/childLearningPdfExport';

/* -------------------- TYPES -------------------- */

interface IChildLearningDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  childId: string | null;
}

/* -------------------- COMPONENT -------------------- */

const ChildLearningDetailsModal = ({
  setIsOpen,
  childId,
}: IChildLearningDetailsModalProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const { loadingModal, setLoadingModal } = useCommonStore();
  const isRTL = useSettingsStore((s) => s.settings.direction === 'rtl');
  const [childData, setChildData] = useState<ISubscriptionChildDetails | null>(
    null
  );

  /* -------------------- EFFECT -------------------- */

  useEffect(() => {
    if (!childId) return;

    const fetchChildDetails = async () => {
      setLoadingModal(true);
      try {
        const response = await getChildDetails(childId);
        if (response?.status) {
          setChildData(response?.data);
        } else {
          console.error(response?.message);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingModal(false);
      }
    };

    fetchChildDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const statusConfig =
    childData?.status && CommonStatusConfig[childData.status];

  const schoolStatusConfig =
    childData?.school?.status && CommonStatusConfig[childData.school.status];

  /* -------------------- SAFE STATS -------------------- */

  const stats = [
    {
      label: 'children.details.full-name',
      value: childData?.full_name ?? '-',
      Icon: IconMoodKid,
    },
    {
      label: 'children.details.gender',
      value:
        childData?.gender === EGender.MALE
          ? formatText('children.details.gender-male')
          : childData?.gender === EGender.FEMALE
            ? formatText('children.details.gender-female')
            : (childData?.gender ?? '-'),
      Icon: IconGenderFemale,
    },
    {
      label: 'children.details.dob',
      value: childData?.date_of_birth
        ? formatUnixToLongDate(childData.date_of_birth)
        : '-',
      Icon: IconCalendarEvent,
    },
    ...(childData?.email
      ? [
          {
            label: 'children.details.email',
            value: childData.email,
            Icon: IconMail,
          },
        ]
      : []),
    {
      label: 'children.details.mother-tongue',
      value:
        String(childData?.mother_tongue).toLowerCase() === 'true'
          ? formatText('common.yes')
          : String(childData?.mother_tongue).toLowerCase() === 'false'
            ? formatText('common.no')
            : (childData?.mother_tongue ?? '-'),
      Icon: IconLanguage,
    },
    {
      label: 'children.details.dialect',
      value: childData?.dialect ?? '-',
      Icon: IconMessageLanguage,
    },
    {
      label: 'children.details.learning-level',
      value:
        childData?.learning_stage &&
        LessonStageConfig[childData?.learning_stage]?.label
          ? formatText(LessonStageConfig[childData?.learning_stage]?.label)
          : (childData?.learning_stage ?? '-'),
      Icon: IconStar,
    },
    {
      label: 'children.details.mode',
      value:
        childData?.parent_subscription_type === EPersonalSubscriptionType.FREE
          ? formatText('children.personal-subscriber.tab.free') +
            ' ' +
            formatText('children.details.mode')
          : childData?.parent_subscription_type ===
              EPersonalSubscriptionType.PAID
            ? formatText('children.personal-subscriber.tab.paid') +
              ' ' +
              formatText('children.details.mode')
            : '-',
      Icon: IconRepeat,
    },
  ];

  const assetUrl = getAssetUrl(childData?.asset);

  const handleExportPDF = async () => {
    if (!childData) return;

    const pdfLabels: IPdfLabels = {
      title: formatText('children.details.title'),
      fullName: formatText('children.details.full-name'),
      gender: formatText('children.details.gender'),
      dob: formatText('children.details.dob'),
      motherTongue: formatText('children.details.mother-tongue'),
      dialect: formatText('children.details.dialect'),
      learningLevel: formatText('children.details.learning-level'),
      mode: formatText('children.details.mode'),
      age: formatText('children.details.age'),
      years: formatText('children.details.years'),
      status: formatText('user-management.status'),
      statusValue: statusConfig?.label ? formatText(statusConfig.label) : '-',
      linkedEntities: formatText('children.details.linked-entities'),
      progressSummary: formatText('children.details.progress-summary'),
      overallProgress: formatText('children.details.overall-progress'),
      starsEarned: formatText('children.details.stars-earned'),
      gamesCompleted: formatText('children.details.games-completed'),
      avgScreenTime: formatText('children.details.avg-screen-time'),
      mins: formatText('common.time.mins'),
      activity: formatText('children.details.activity'),
      noDataFound: formatText('common.no-data-found'),
      licenseKey: formatText('school.school-detail.license-key'),
      assignedTeacher: formatText('school.school-detail.assigned-teacher'),
      licenseExpiry: formatText('school.school-detail.license-expiry'),
      learningStageLabel:
        childData.learning_stage &&
        LessonStageConfig[childData.learning_stage]?.label
          ? formatText(LessonStageConfig[childData.learning_stage].label)
          : String(childData.learning_stage ?? '-'),
      subscriptionTypeLabel:
        childData.subscription_type === EPersonalSubscriptionType.FREE
          ? formatText('children.personal-subscriber.tab.free')
          : formatText('children.personal-subscriber.tab.paid'),
      childrenLabel: formatText('sidebar.children'),
      learningDetails: formatText('children.edit.learning-details'),
    };

    const pdfStats: IPdfStatItem[] = stats.map((s) => ({
      label: formatText(s.label),
      value: typeof s.value === 'string' ? s.value : String(s.value),
    }));

    const formattedActivities = (childData.activity_timeline ?? []).map(
      (a: IActivityTimeline) => ({
        title: a.title,
        timeLabel: formatLastActive(a.timestamp, formatText),
        quarters: a.quarters,
        rounds: a.rounds,
      })
    );

    try {
      await exportChildLearningPdf(
        childData,
        pdfLabels,
        pdfStats,
        formattedActivities,
        assetUrl,
        isRTL
      );
    } catch (error) {
      console.error('PDF export failed:', error);
    }
  };

  /* -------------------- RENDER -------------------- */

  if (!hasAccess(ERoleAccess.REPORTS_ANALYTICS)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      heading={formatText('children.details.title')}
      position="right"
      className="max-w-150"
    >
      {loadingModal || !childData ? (
        <UserDetailsSkeleton />
      ) : (
        <div className="space-y-10 bg-white w-full">
          {/* PROFILE SECTION */}
          <div className="flex flex-col items-center gap-4">
            <AvatarGroupList
              list={[
                {
                  id: childData?.id || '',
                  name:
                    childData?.full_name ??
                    formatText('children.details.child-avatar'),
                  image: childData?.asset ? assetUrl : '',
                },
              ]}
              size={100}
              avatarClassName="w-25! h-25! text-4xl!"
              className="rounded-full overflow-hidden"
            />

            <div className="text-2xl font-bold text-neutral-primary">
              {childData.full_name}
            </div>

            <div className="flex gap-2">
              {childData?.learning_stage && (
                <span
                  className={clsx(
                    'px-2 py-1 rounded-full text-xs font-semibold',
                    LessonStageConfig[childData?.learning_stage]?.className
                  )}
                >
                  {LessonStageConfig[childData?.learning_stage]?.label
                    ? formatText(
                        LessonStageConfig[childData?.learning_stage]?.label
                      )
                    : childData?.learning_stage}
                </span>
              )}
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-neutral-light-100 text-neutral-secondary">
                {childData.subscription_type === EPersonalSubscriptionType.FREE
                  ? formatText('children.personal-subscriber.tab.free')
                  : formatText('children.personal-subscriber.tab.paid')}
              </span>
            </div>

            <div className="flex gap-4 text-xs text-neutral-secondary">
              <div>
                {formatText('children.details.age')}:{' '}
                <span className="font-medium text-neutral-primary px-1">
                  {childData?.age ?? 0} {formatText('children.details.years')}
                </span>
              </div>
              <div>
                {formatText('children.details.gender')}:{' '}
                <span className="font-medium text-neutral-primary px-1">
                  {childData?.gender === EGender.MALE
                    ? formatText('children.details.gender-male')
                    : childData?.gender === EGender.FEMALE
                      ? formatText('children.details.gender-female')
                      : (childData?.gender ?? '-')}
                </span>
              </div>
            </div>
          </div>

          {/* STATUS ROW */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={childData.status === ECommonStatus.ACTIVE}
                onChange={() => {}}
                disabled
              />
              <span className="text-sm font-medium text-neutral-secondary">
                {formatText('user-management.status')}:{' '}
                <span className="text-neutral-primary">
                  {statusConfig?.label ? formatText(statusConfig.label) : '-'}
                </span>
              </span>
            </div>

            <Button
              label={formatText('common.actions.export-data')}
              icon={<IconFileExport size={18} />}
              variant="outline"
              className="px-4 h-10 text-sm font-medium rounded-lg"
              onClick={handleExportPDF}
              data-ignore-export="true"
            />
          </div>

          {/* STATS GRID */}
          <div className="grid grid-cols-2 gap-y-6 gap-x-12">
            {stats?.map(({ label, value, Icon }) => (
              <div key={label} className="flex items-start gap-3 min-w-0">
                <div className="mt-0.5 text-neutral-secondary shrink-0">
                  <Icon size={20} />
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="text-sm text-neutral-secondary">
                    {formatText(label)}
                  </div>
                  <div className="text-base font-semibold text-neutral-primary wrap-anywhere">
                    {value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* LINKED ENTITIES */}
          {!!childData?.linked_entities?.length && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-neutral-primary">
                  {formatText('children.details.linked-entities')}
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-neutral-light-100 text-xs font-semibold text-neutral-secondary">
                  {childData.linked_entities.length}
                </span>
              </div>
              {childData.linked_entities.map((entity) =>
                childData?.role === EChildRole.CHILD ? (
                  <div
                    key={entity?.id}
                    className="rounded-2xl border border-neutral-light-200 p-4 space-y-2"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-neutral-primary font-semibold">
                        {entity?.name ?? '-'}
                      </span>
                      <span className="text-neutral-secondary text-sm">
                        {entity?.email ?? '-'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    key={entity?.id}
                    className="bg-brand-light-50 rounded-2xl p-6 space-y-6"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-brand-default rounded-lg">
                          <IconSchool size={24} className="text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-neutral-primary">
                          {entity?.name ?? '-'}
                        </h3>
                        <span
                          className={clsx(
                            'px-2 py-0.5 text-xs font-bold rounded-full',
                            schoolStatusConfig?.className
                          )}
                        >
                          {schoolStatusConfig?.label
                            ? formatText(schoolStatusConfig.label)
                            : '-'}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-neutral-secondary font-medium">
                          {formatText('school.school-detail.license-key')}:
                        </span>
                        <span className="bg-white px-2 py-0.5 rounded-md font-bold text-neutral-primary uppercase tracking-wider">
                          {entity?.license_key ?? '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-secondary font-medium">
                          {formatText('school.school-detail.assigned-teacher')}:
                        </span>
                        <span className="font-bold text-neutral-primary">
                          {entity?.assigned_teacher?.name ?? '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-secondary font-medium">
                          {formatText(
                            'school.school-detail.subscription-expiry'
                          )}
                          :
                        </span>
                        <span className="font-bold text-neutral-primary">
                          {entity?.expiry_date
                            ? formatDate(entity?.expiry_date)
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* PROGRESS SUMMARY */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-primary">
              {formatText('children.details.progress-summary')}
            </h3>

            <div className="rounded-2xl border border-neutral-light-200 p-4 space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-secondary font-bold">
                    {formatText('children.details.overall-progress')}
                  </span>
                  <span className="text-neutral-primary font-bold">
                    {childData?.progress_summary?.overall_progress ?? 0}%
                  </span>
                </div>
                <div className="h-2 bg-neutral-light-100 rounded-full w-full">
                  <div
                    className="h-2 bg-brand-default rounded-full transition-all duration-500"
                    style={{
                      width: `${childData?.progress_summary?.overall_progress ?? 0}%`,
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-warning-light-100 p-4 rounded-lg flex flex-col items-center gap-1">
                  <span className="text-xs text-warning-secondary font-bold text-center">
                    {formatText('children.details.stars-earned')}
                  </span>
                  <div className="flex items-center gap-1">
                    <IconStarFilled
                      size={20}
                      className="text-warning-secondary"
                    />
                    <span className="text-xl font-bold text-warning-secondary">
                      {childData?.progress_summary?.stars_earned ?? 0}
                    </span>
                  </div>
                </div>

                <div className="bg-success-light-50 p-4 rounded-lg flex flex-col items-center gap-1 text-center">
                  <span className="text-xs text-success-secondary font-bold">
                    {formatText(
                      childData?.role === EChildRole.STUDENT
                        ? 'children.details.lessons-completed'
                        : 'children.details.games-completed'
                    )}
                  </span>
                  <span className="text-xl font-bold text-success-secondary">
                    {childData?.role === EChildRole.STUDENT
                      ? (childData?.progress_summary?.lessons_completed ?? 0)
                      : (childData?.progress_summary?.games_completed ?? 0)}
                  </span>
                </div>

                <div className="bg-indigo-light-50 p-4 rounded-lg flex flex-col items-center gap-1 text-center">
                  <span className="text-xs text-indigo-secondary font-bold">
                    {formatText('children.details.avg-screen-time')}
                  </span>
                  <span className="text-xl font-bold text-indigo-900">
                    {formatSecondsToDuration(
                      childData?.progress_summary?.average_screen_time
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ACTIVITY TIMELINE */}
          <div className="inline-flex flex-col gap-4 w-full">
            <div className="border-b border-neutral-200 pb-1">
              <h3 className="text-lg font-semibold text-neutral-primary leading-7">
                {formatText('children.details.activity')}
              </h3>
            </div>
            <div className="relative flex flex-col gap-6">
              {childData?.activity_timeline?.length ? (
                childData?.activity_timeline?.map(
                  (a: IActivityTimeline, i: number) => (
                    <>
                      {/* Vertical line */}
                      <div className="absolute left-5 rtl:right-5 rtl:left-auto top-0 h-full w-px bg-neutral-200" />
                      <div key={i} className="flex items-center gap-4">
                        {/* Timeline Dot */}
                        <div className="relative z-10 flex p-2 items-center justify-center rounded-full border border-neutral-300 bg-white">
                          <IconCircleCheckFilled
                            size={24}
                            className="text-brand-default"
                          />
                        </div>

                        {/* Content */}
                        <div className="flex flex-1 items-center justify-between gap-2">
                          <div className="space-x-2 min-w-0">
                            <span className="text-base font-semibold text-neutral-primary max-w-80 wrap-break-word">
                              {a?.title}
                            </span>
                            {a?.quarters !== null &&
                              a?.quarters !== undefined && (
                                <TagCapsule
                                  value={`Q${a.quarters}`}
                                  className="bg-purple-50 text-brand-default"
                                />
                              )}
                            {a?.rounds !== null && a?.rounds !== undefined && (
                              <TagCapsule
                                value={`R${a.rounds}`}
                                className="bg-neutral-100 text-neutral-primary"
                              />
                            )}
                          </div>
                          <span className="text-sm text-neutral-secondary shrink-0">
                            {formatLastActive(a?.timestamp, formatText)}
                          </span>
                        </div>
                      </div>
                    </>
                  )
                )
              ) : (
                <div className="flex items-center flex-col gap-3 justify-center w-full px-0 pt-10">
                  <CustomImage
                    src={'/assets/images/lesson-breakdown-empty.png'}
                    alt="noData"
                    width={100}
                    height={100}
                  />
                  <p className="text-neutral-900 text-xl font-semibold sm:text-nowrap ">
                    {formatText('common.no-data-found')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default ChildLearningDetailsModal;
